import React, { useEffect, useState, useMemo } from 'react';
import Select from 'react-select';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link, Redirect } from 'react-router-dom';
import { getHistoryBySerieId, clearSerieHistory, GET_SERIE_HISTORY } from '../../redux/actions/Question/Serie/SerieGet/serieGet.actions';
import { selectSerieHistory } from '../../redux/selectors/History/history.selector';
import { createLoadingSelectorForStoreValue } from '../../redux/selectors/APISelector/APISelector';
import SerieHistoryTable from './SerieHistoryTable';
import SerieHistoryBySkillChart from './SerieHistoryBySkillChart';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { IconButton } from '@material-ui/core';
import { QUESTIONS_PAGE_URL } from 'app/Routes';
import { getSerieByIdWithQuestionReducedApi } from '../../api/seriesApi';
import { notificationError } from 'app/redux/actions/Notification/notifications.actions';
import { selectCurrentUser } from 'app/redux/selectors/User/user.selector';
import { REDIRECT_EXCILYENS_ACCESS } from 'app/Snackbar/NotificationMessages';

import './SerieHistoryPage.scss';

export default function SerieHistoryPage() {
    const { serieId } = useParams();
    const dispatch = useDispatch();
    const serieHistory = useSelector(selectSerieHistory);
    const isFetchingHistory = useSelector(createLoadingSelectorForStoreValue([GET_SERIE_HISTORY], selectSerieHistory));
    const [serieTitle, setSerieTitle] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [showActiveUsersOnly, setShowActiveUsersOnly] = useState(false);

    const flattenedHistory = useMemo(() => {
        if(!Array.isArray(serieHistory)) {
            return [];
        }
        return serieHistory.flatMap((userHistory) => (userHistory.seriesHistoryBySkill || []).filter((historyEntry) => historyEntry.score > 0).map((historyEntry) => ({
            id: historyEntry.serieHistoryId,
            user: userHistory.user,
            score: historyEntry.score,
            date: historyEntry.date,
            scoresBySkill: historyEntry.historySerieGrouped || []
        })));
    }, [serieHistory]);

    const userOptions = useMemo(() => {
        if(!Array.isArray(serieHistory)) {
            return [];
        }
        return serieHistory.map((userHistory) => ({
            value: userHistory.user.id,
            label: `${userHistory.user.firstName} ${userHistory.user.lastName}`
        }));
    }, [serieHistory]);

    const filteredHistory = useMemo(() => flattenedHistory.filter((entry) => {
        const entryDate = new Date(entry.date);
        const start = startDate ? new Date(startDate) : null;
        const end = endDate ? new Date(endDate) : null;

        if(start) {
            start.setHours(0, 0, 0, 0);
        }
        if(end) {
            end.setHours(23, 59, 59, 999);
        }

        const dateFilter = (!start || entryDate >= start) && (!end || entryDate <= end);
        const userFilter = selectedUsers.length === 0 || selectedUsers.some((user) => user.value === entry.user.id);

        return dateFilter && userFilter;
    }), [flattenedHistory, startDate, endDate, selectedUsers]);

    const [redirect, setRedirect] = useState(false);

    const checkForAdmin = (user) => {
        const isRecruiter = (user.roles && user.roles.some((role) => role.name === 'RECRUTEUR'));
        if(!isRecruiter) {
            dispatch(notificationError(REDIRECT_EXCILYENS_ACCESS));
        }
        setRedirect(!isRecruiter);
    };

    useEffect(() => {
        dispatch(clearSerieHistory());
        dispatch(getHistoryBySerieId(serieId, showActiveUsersOnly));
        getSerieByIdWithQuestionReducedApi(serieId).then((response) => {
            setSerieTitle(response.data.title);
        });
    }, [dispatch, serieId, showActiveUsersOnly]);

    const currentUser = useSelector(selectCurrentUser);

    useEffect(() => {
        checkForAdmin(currentUser);
    }, [currentUser.id]);

    return (
        <>
            {redirect ? <Redirect to={'/skills'} /> : <></>}
            {
                <div className="serie-history-page">
                    <div className="header">
                        <Link to={QUESTIONS_PAGE_URL}>
                            <IconButton aria-label="retour">
                                <ArrowBackIcon />
                            </IconButton>
                        </Link>
                        <h1>Historique de la série {serieTitle}</h1>
                    </div>
                    <div className="filters">
                        <div className="date-filter">
                            <label htmlFor="start-date">Début : </label>
                            <input
                                type="date"
                                id="start-date"
                                value={startDate}
                                onChange={(event) => setStartDate(event.target.value)}
                            />
                            <label htmlFor="end-date"> Fin : </label>
                            <input
                                type="date"
                                id="end-date"
                                value={endDate}
                                onChange={(event) => setEndDate(event.target.value)}
                            />
                        </div>
                        <div className="user-filter">
                            <Select
                                isMulti
                                id="user-select"
                                options={userOptions}
                                value={selectedUsers}
                                onChange={(users) => setSelectedUsers(users || [])}
                                placeholder="Oxyliens"
                                hideSelectedOptions={false}
                                closeMenuOnSelect={false}
                                getOptionValue={(option) => option.value}
                                getOptionLabel={(option) => option.label}
                            />
                        </div>
                        <div className="active-user-filter">
                            <input
                                type="checkbox"
                                id="active-users-only"
                                checked={showActiveUsersOnly}
                                onChange={(event) => setShowActiveUsersOnly(event.target.checked)}
                            />
                            <label htmlFor="active-users-only">Actifs uniquement</label>
                        </div>
                    </div>
                    <div>
                        {isFetchingHistory
                            ? (
                                <p>Chargement de l&apos;historique...</p>
                            )
                            : (
                                <SerieHistoryTable history={filteredHistory} />
                            )}
                    </div>
                    <div>
                        <SerieHistoryBySkillChart serieHistory={filteredHistory}/>
                    </div>
                </div>
            }
        </>
    );
}
